/**
 * Session state, held in MEMORY only.
 *
 * Nothing here touches localStorage. The refresh token is an HttpOnly cookie
 * the browser holds and this code can never read; the access token lives in
 * the variable below, expires in fifteen minutes, and dies with the tab.
 *
 * That is the whole point. Previously both tokens sat in localStorage, where
 * any script running on the page — an injected one, a compromised dependency,
 * a stored-XSS field somebody forgot to escape — could read a thirty-day
 * refresh token and walk off with the account. Rotation did not help: the
 * thief refreshes first and the reuse detector logs out the victim.
 *
 * The cost is one round trip on every page load: memory is empty, so the app
 * asks the cookie to prove who it is before rendering. `restoring` is what
 * stops that showing as a flash of the signed-out site.
 *
 * The call itself is in `refresh.ts`, not here. This file is a pure store —
 * which is what lets `refresh.ts` import it without a cycle.
 */
import type { Session } from './api';

let current: Session | null = null;
/** True until the boot-time refresh has resolved one way or the other. */
let restoring = true;

const listeners = new Set<() => void>();

export const getSession = (): Session | null => current;
export const isRestoring = (): boolean => restoring;

function emit() {
  for (const listener of listeners) listener();
}

export function setSession(session: Session): void {
  current = session;
  restoring = false;
  emit();
}

export function clearSession(): void {
  current = null;
  restoring = false;
  emit();
}

/** Called once at boot when there turns out to be no session to restore. */
export function finishRestoring(): void {
  restoring = false;
  emit();
}

/** Subscribe for React's useSyncExternalStore. */
export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
